/**
 * Finance V2 — Capital Structures CRUD.
 * User-editable financing structures with clear Actual vs Modeled provenance.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Plus, Trash2, Edit3, Check, X } from "lucide-react";
import { Section, CategoryChip, fmtMoney0 } from "./_shared";

const empty = {
  name: "", provenance: "modeled",
  facility_amount: null,
  annual_cost_of_capital_pct: "",
  origination_fee_pct: 0,
  servicing_fee_pct_annual: 0,
  fixed_annual_fee: 0,
  hurdle_pct: 0,
  profit_split_pct: 0,
  term_months: null,
  notes: "",
  is_current: false,
};

export default function FinanceCapitalStructures() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/v2/capital-structures");
      setRows(data.structures || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (r) => { setEditing(r.id); setForm({ ...empty, ...r }); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async () => {
    // Prevent silent "0%" or blank rates from being persisted.
    // Every configured structure must state its rate explicitly.
    if (form.annual_cost_of_capital_pct === "" || form.annual_cost_of_capital_pct === null) {
      toast.error("Annual Cost of Capital is required. Enter the actual or modeled rate.");
      return;
    }
    if (!form.name || !form.name.trim()) {
      toast.error("Structure name is required.");
      return;
    }
    try {
      if (editing === "new") {
        await api.post("/finance/v2/capital-structures", form);
        toast.success("Capital structure created");
      } else {
        await api.patch(`/finance/v2/capital-structures/${editing}`, form);
        toast.success("Capital structure updated");
      }
      cancel(); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete capital structure "${r.name}"?`)) return;
    try { await api.delete(`/finance/v2/capital-structures/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="finance-capital-structures-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0A2540]">Capital Structures</h1>
          <div className="text-xs text-[#6B7280] mt-1">
            Define one or more financing structures. Every metric that depends on cost of capital references the currently-selected structure.
          </div>
        </div>
        <button onClick={startNew} data-testid="new-structure-btn"
                className="h-9 px-4 rounded-md bg-[#0A2540] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#0F3564]">
          <Plus className="w-4 h-4" /> New structure
        </button>
      </div>

      <Section title="Configured structures" category="mixed"
        subtitle="Structures tagged Actual reflect signed contract terms. Modeled structures are user-entered forward assumptions.">
        {loading ? <div className="text-sm text-[#6B7280]">Loading…</div> :
          rows.length === 0 ? (
            <div className="rounded-md bg-white border border-[#E5E7EB] p-4 text-sm text-[#6B7280]" data-testid="no-structures">
              No capital structures configured yet. Cost-of-capital-dependent metrics (Contribution, Pro Forma, Sensitivity, Capital Comparison) will show <b>Not Configured</b> until you add an Actual or Modeled structure. Finance V2 does not assume any default cost of capital.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
                  <th className="py-2">Name</th><th>Category</th><th>Facility</th><th>Cost / yr</th>
                  <th>Origination</th><th>Servicing / yr</th><th>Current</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#F3F4F6]" data-testid={`row-${r.id}`}>
                    <td className="py-2 pr-4">{r.name}</td>
                    <td><CategoryChip category={r.provenance === "actual" ? "contractual" : "forecast"} /></td>
                    <td className="tabular-nums">{fmtMoney0(r.facility_amount)}</td>
                    <td className="tabular-nums">{r.annual_cost_of_capital_pct}%</td>
                    <td className="tabular-nums">{r.origination_fee_pct}%</td>
                    <td className="tabular-nums">{r.servicing_fee_pct_annual}%</td>
                    <td>{r.is_current ? <span className="text-[#065F46] text-xs font-medium">✓ Current</span> : "—"}</td>
                    <td className="text-right">
                      <button onClick={() => startEdit(r)} data-testid={`edit-${r.id}`}
                              className="h-8 px-2 text-xs mr-1 hover:bg-[#F3F4F6] rounded"><Edit3 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(r)} data-testid={`del-${r.id}`}
                              className="h-8 px-2 text-xs hover:bg-[#FEE2E2] rounded text-[#991B1B]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Section>

      {editing && (
        <Section title={editing === "new" ? "New capital structure" : `Edit: ${form.name}`} category="forecast">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="input" data-testid="form-name" placeholder="e.g. Proposed Facility A" />
            </Field>
            <Field label="Provenance">
              <select value={form.provenance} onChange={(e) => setForm({ ...form, provenance: e.target.value })}
                      className="input" data-testid="form-provenance">
                <option value="modeled">Modeled / Assumed</option>
                <option value="actual">Actual / Contractual</option>
              </select>
            </Field>
            <Field label="Facility amount ($)">
              <input type="number" value={form.facility_amount ?? ""}
                     onChange={(e) => setForm({ ...form, facility_amount: e.target.value === "" ? null : parseFloat(e.target.value) })}
                     className="input tabular-nums" data-testid="form-facility" />
            </Field>
            <Field label="Annual cost of capital (%)">
              <input type="number" step="0.1" value={form.annual_cost_of_capital_pct}
                     onChange={(e) => setForm({ ...form, annual_cost_of_capital_pct: parseFloat(e.target.value || 0) })}
                     className="input tabular-nums" data-testid="form-coc" />
            </Field>
            <Field label="Origination fee (% of each deployment)">
              <input type="number" step="0.1" value={form.origination_fee_pct}
                     onChange={(e) => setForm({ ...form, origination_fee_pct: parseFloat(e.target.value || 0) })}
                     className="input tabular-nums" />
            </Field>
            <Field label="Servicing fee (%/yr of outstanding)">
              <input type="number" step="0.1" value={form.servicing_fee_pct_annual}
                     onChange={(e) => setForm({ ...form, servicing_fee_pct_annual: parseFloat(e.target.value || 0) })}
                     className="input tabular-nums" />
            </Field>
            <Field label="Fixed annual fee ($)">
              <input type="number" value={form.fixed_annual_fee}
                     onChange={(e) => setForm({ ...form, fixed_annual_fee: parseFloat(e.target.value || 0) })}
                     className="input tabular-nums" />
            </Field>
            <Field label="Hurdle rate (%)">
              <input type="number" step="0.1" value={form.hurdle_pct}
                     onChange={(e) => setForm({ ...form, hurdle_pct: parseFloat(e.target.value || 0) })}
                     className="input tabular-nums" />
            </Field>
            <Field label="Profit split above hurdle (%)">
              <input type="number" step="0.1" value={form.profit_split_pct}
                     onChange={(e) => setForm({ ...form, profit_split_pct: parseFloat(e.target.value || 0) })}
                     className="input tabular-nums" />
            </Field>
            <Field label="Term (months)">
              <input type="number" value={form.term_months ?? ""}
                     onChange={(e) => setForm({ ...form, term_months: e.target.value === "" ? null : parseInt(e.target.value) })}
                     className="input tabular-nums" />
            </Field>
            <Field label="Notes" className="col-span-2">
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={2} className="input" />
            </Field>
            <Field label=" ">
              <label className="text-sm flex items-center gap-2 mt-1">
                <input type="checkbox" checked={form.is_current} onChange={(e) => setForm({ ...form, is_current: e.target.checked })} data-testid="form-is-current" />
                Mark as the currently-live structure
              </label>
            </Field>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} data-testid="save-btn"
                    className="h-9 px-4 rounded-md bg-[#0A2540] text-white text-sm font-medium flex items-center gap-2">
              <Check className="w-4 h-4" /> Save
            </button>
            <button onClick={cancel} className="h-9 px-4 rounded-md bg-white border border-[#E5E7EB] text-sm font-medium flex items-center gap-2">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </Section>
      )}
      <style>{`
        .input { display: block; width: 100%; height: 36px; padding: 0 10px;
                  border: 1px solid #E5E7EB; border-radius: 6px; background: white; font-size: 13px; }
        textarea.input { height: auto; padding: 8px 10px; }
      `}</style>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">{label}</label>
    {children}
  </div>
);
